import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Button,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import imageUpload from "assets/icons/imageUpload.svg";
import { driverRequestReasons } from "enums/shared";

const DriverChangeRequestModal = ({ open, onClose, onSubmit, t, isRTL }) => {
  const [reason, setReason] = useState("");
  const [explanation, setExplanation] = useState("");
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);

  const reasons = Object.entries(driverRequestReasons).map(
    ([value, labelKey]) => ({
      value: value,
      label: t(labelKey),
    })
  );

  useEffect(() => {
    if (!open) {
      setReason("");
      setExplanation("");
      setFiles([]);
    }
  }, [open]);

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);
    setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
  };

  const handleRemoveFile = (fileToRemove) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file !== fileToRemove));
  };

  const handleSave = () => {
    onSubmit({
      reason: Number(reason),
      explanation,
      files,
    });
  };

  const isSaveDisabled = !reason || (reason === "0" && !explanation.trim());

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "24px",
        },
      }}
    >
      <DialogTitle>
        {t("driverChangeRequest")}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: isRTL ? "auto" : 8,
            left: isRTL ? 8 : "auto",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography>
          <b>{t("reasonForOrderUpdate")}</b> {t("driverChangePrompt")}
        </Typography>
        <FormControl component="fieldset" sx={{ width: "100%", mt: 2 }}>
          <RadioGroup
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          >
            {reasons.map((r) => (
              <Box key={r.value}>
                <FormControlLabel
                  value={r.value}
                  control={
                    <Radio sx={{ "&.Mui-checked": { color: "#2A3467" } }} />
                  }
                  label={r.label}
                />
                {reason === "0" && r.value === "0" && (
                  <Box sx={{ ml: 4, width: "calc(100% - 32px)" }}>
                    <TextField
                      autoFocus
                      fullWidth
                      variant="outlined"
                      placeholder={t("explainReason")}
                      value={explanation}
                      onChange={(e) => setExplanation(e.target.value)}
                      sx={{
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                          { borderColor: "#2A3467" },
                      }}
                    />
                    <input
                      type="file"
                      multiple
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      style={{ display: "none" }}
                    />
                    <Box
                      onClick={() => fileInputRef.current.click()}
                      sx={{
                        border: "1px dashed grey",
                        borderRadius: 1,
                        p: 2,
                        mt: 2,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: "bold",
                          }}
                        >
                          {t("attachmentForRequest")}
                        </Typography>
                        <Typography>{t("uploadDocuments")}</Typography>
                      </Box>
                      <UploadFileIcon sx={{ color: "grey.700" }} />
                    </Box>
                    <Box sx={{ mt: 1 }}>
                      {files.map((file, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1,
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <img
                              src={imageUpload}
                              alt="document icon"
                              style={{ width: "32px", height: "35px" }}
                            />
                            <Typography variant="body2">{file.name}</Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveFile(file)}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            ))}
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleSave}
          variant="outlined"
          disabled={isSaveDisabled}
          sx={{
            color: "#fff",
            borderColor: "#2A3467",
            padding: "8px 35px",
            backgroundColor: "#2A3467",
            "&:hover": {
              backgroundColor: "#fff",
              color: "#2A3467",
              borderColor: "#2A3467",
            },

            "&:disabled": {
              backgroundColor: "#cccccc",
              color: "#888888",
              borderColor: "#cccccc",
            },
          }}
        >
          {t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DriverChangeRequestModal;
